import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSession } from './lib/queries';
import { Header } from './components/header';
import { ScrollToTop } from './components/scroll-to-top';
import { LoginPage } from './pages/login';
import { SearchPage } from './pages/search';
import { ArticlePage } from './pages/article';
import { AdminDashboardPage } from './pages/admin/dashboard';
import { AdminArticlesPage } from './pages/admin/articles';
import { AdminArticleEditPage } from './pages/admin/article-edit';
import { AdminUsersPage } from './pages/admin/users';
import { AdminUserNewPage } from './pages/admin/user-new';
import { AdminUserEditPage } from './pages/admin/user-edit';
import type { ReactNode } from 'react';

function CercaRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/${search}`} replace />;
}

function ProtectedRoute({ children, adminOnly = false }: { children: ReactNode; adminOnly?: boolean }) {
  const { data: session, isLoading } = useSession();
  const location = useLocation();

  if (isLoading) {
    return <div className="container-narrow py-12 text-center text-sm text-(--color-muted-foreground)">Caricamento…</div>;
  }
  if (!session) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  if (adminOnly && session.user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[72rem] flex-col bg-(--color-background) shadow-sm">
      <Header />
      <main className="flex-1">
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <SearchPage />
              </ProtectedRoute>
            }
          />
          <Route path="/cerca" element={<CercaRedirect />} />
          <Route
            path="/articoli/:id"
            element={
              <ProtectedRoute>
                <ArticlePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/articoli"
            element={
              <ProtectedRoute adminOnly>
                <AdminArticlesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/articoli/nuovo"
            element={
              <ProtectedRoute adminOnly>
                <AdminArticleEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/articoli/:id"
            element={
              <ProtectedRoute adminOnly>
                <AdminArticleEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/utenti"
            element={
              <ProtectedRoute adminOnly>
                <AdminUsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/utenti/nuovo"
            element={
              <ProtectedRoute adminOnly>
                <AdminUserNewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/utenti/:id"
            element={
              <ProtectedRoute adminOnly>
                <AdminUserEditPage />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <footer className="border-t-2 border-(--color-brand-sage)/40 py-6 text-center text-xs text-(--color-muted-foreground)">
        Ceccarius · {new Date().getFullYear()}
      </footer>
      <ScrollToTop />
    </div>
  );
}
